import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface QuoteAcceptanceButtonProps {
  leadId: string;
  currentStatus: string;
}

export const QuoteAcceptanceButton = ({ leadId, currentStatus }: QuoteAcceptanceButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const queryClient = useQueryClient();

  const acceptQuoteMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the latest quotation for this lead
      const { data: quotation, error: quotationError } = await supabase
        .from("quotations")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (quotationError) throw quotationError;
      if (!quotation) throw new Error("No quotation found for this lead");

      // Get itinerary items for snapshot
      const { data: itineraryItems, error: itemsError } = await supabase
        .from("itinerary_items")
        .select("*")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: true });

      if (itemsError) throw itemsError;

      // Check if this is the first revision
      const { data: existingRevisions, error: revisionCheckError } = await supabase
        .from("quote_revisions")
        .select("revision_number")
        .eq("quotation_id", quotation.id)
        .order("revision_number", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (revisionCheckError) throw revisionCheckError;

      const nextRevisionNumber = existingRevisions ? existingRevisions.revision_number + 1 : 1;

      // Create first revision snapshot
      const { error: revisionError } = await supabase
        .from("quote_revisions")
        .insert({
          quotation_id: quotation.id,
          lead_id: leadId,
          user_id: user.id,
          revision_number: nextRevisionNumber,
          quote_number: quotation.quote_number,
          subtotal: quotation.subtotal,
          markup_percentage: quotation.markup_percentage,
          markup_amount: quotation.markup_amount,
          total_amount: quotation.total_amount,
          valid_until: quotation.valid_until,
          terms_and_conditions: quotation.terms_and_conditions,
          notes: quotation.notes,
          status: "accepted",
          itinerary_snapshot: itineraryItems || [],
          change_summary: nextRevisionNumber === 1 ? "Initial quote accepted by client" : "Quote re-accepted with modifications",
        });

      if (revisionError) throw revisionError;

      // Mark the quotation itself as accepted
      const { error: quoteError } = await supabase
        .from("quotations")
        .update({ status: "accepted" })
        .eq("id", quotation.id);

      if (quoteError) throw quoteError;

      // Update lead status to quote_accepted and link the accepted quotation
      const { error } = await supabase
        .from("leads")
        .update({ 
          status: "quote_accepted",
          accepted_quotation_id: quotation.id 
        })
        .eq("id", leadId);

      if (error) throw error;

      // Log the activity
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Quote Accepted",
        details: `Client has accepted the quote (Version ${nextRevisionNumber})`,
      });

      // Log the status change
      await supabase.from("lead_changes").insert({
        lead_id: leadId,
        user_id: user.id,
        field_changed: "status",
        old_value: currentStatus,
        new_value: "quote_accepted",
      });

      // Notify the consultant + admins (bell notification + email)
      try {
        await supabase.functions.invoke("notify-quote-accepted", {
          body: { quotationId: quotation.id, route: "staff" },
        });
      } catch (notifyErr) {
        console.error("Acceptance notification failed:", notifyErr);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", leadId] });
      toast.success("Quote marked as accepted!");
      setShowDialog(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to update status: ${error.message}`);
    },
  });

  // Only show button if status is quote_sent
  if (currentStatus !== "quote_sent") return null;

  return (
    <>
      <Button 
        onClick={() => setShowDialog(true)}
        className="gap-2"
        variant="default"
      >
        <CheckCircle className="h-4 w-4" />
        Quote Accepted
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Quote as Accepted?</AlertDialogTitle>
            <AlertDialogDescription>
              This will update the lead status to "Quote Accepted", indicating that the client has accepted the quote. You can still make edits to the itinerary after acceptance if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => acceptQuoteMutation.mutate()}
              disabled={acceptQuoteMutation.isPending}
            >
              {acceptQuoteMutation.isPending ? "Updating..." : "Confirm Acceptance"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
